import { motion } from 'framer-motion'
import { Send, CheckCircle, AlertCircle, User, Mail, Phone, Briefcase, MessageSquare, Calendar } from 'lucide-react'
import { useState } from 'react'
import { useLanguage } from '../context/LanguageContext'
import emailjs from '@emailjs/browser'
import SEO from '../components/SEO'
import { db } from '../firebase'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

const Apply = () => {
  const { t } = useLanguage()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      // Save to Firebase
      await addDoc(collection(db, 'projectApplications'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        projectType: formData.projectType,
        budget: formData.budget,
        timeline: formData.timeline,
        description: formData.description,
        createdAt: serverTimestamp(),
        status: 'new'
      })
      
      // Send email
      const result = await emailjs.send(
        'service_tn8xlnj',
        'template_anw5r65',
        {
          name: formData.name,
          user_email: formData.email,
          message: `
📱 Phone: ${formData.phone}
🏢 Company: ${formData.company}
🎯 Project Type: ${formData.projectType}
💰 Budget: ${formData.budget}
📅 Timeline: ${formData.timeline}

📝 Description:
${formData.description}
          `
        },
        'Ac73yOgPaJV0tu4Ri'
      )
      
      console.log('EmailJS Success:', result)
      setSubmitStatus('success')
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        projectType: '',
        budget: '',
        timeline: '',
        description: ''
      })
    } catch (error) {
      console.error('EmailJS Error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
      setTimeout(() => setSubmitStatus(null), 5000)
    }
  }

  const projectTypes = [
    'Web Development',
    'Social Media Management',
    'Content Creation',
    'Logo & Branding Design',
    'SEO Optimization',
    'Full Digital Marketing Package',
    'Other'
  ]

  const budgetRanges = [
    'Under €500',
    '€500 - €1,000',
    '€1,000 - €2,500',
    '€2,500 - €5,000',
    'Over €5,000'
  ]

  const timelineOptions = [
    'ASAP (Within 1 week)',
    '2-4 weeks',
    '1-2 months',
    '2-3 months',
    'Flexible'
  ]

  return (
    <>
      <SEO 
        title="Apliko për Projekt - Contenta Marketing"
        description="Dërgo aplikimin tënd për projektin e ardhshëm. Na trego idetë dhe ne do të të ndihmojmë t'i realizosh ato."
        keywords="apliko projekt, digital marketing albania, web development albania, contenta marketing"
      />
      
      <div className="mt-20">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-bronze-500/5 rounded-full blur-3xl" />
          </div>

          <div className="container mx-auto px-4 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="inline-block mb-4"
              >
                <div className="px-6 py-2 bg-gold-500/10 border border-gold-400/30 rounded-full backdrop-blur-sm">
                  <span className="text-gold-400 font-semibold">Filloni Projektin Tuaj</span>
                </div>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-display font-bold mb-6">
                Transformojmë <span className="text-gradient-gold">Idenë Tuaj</span><br />
                në Realitet Dixhital
              </h1>
              <p className="text-xl text-beige-200 max-w-3xl mx-auto leading-relaxed">
                Plotëso formularin më poshtë dhe ekipi ynë do të të kontaktojë brenda 24 orëve për të diskutuar projektin tënd.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Application Form */}
        <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="card-premium">
                <h2 className="text-3xl font-display font-bold text-cream mb-2">
                  Aplikimi për Projekt
                </h2>
                <p className="text-beige-200 mb-8">
                  Sa më shumë detaje të na jepësh, aq më mirë do të të ndihmojmë.
                </p>

                {/* Success Message */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-green-500/20 border border-green-500 rounded-lg flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <div>
                      <p className="text-green-400 font-semibold">Aplikimi u dërgua me sukses!</p>
                      <p className="text-green-300 text-sm">Do të kontaktohemi me ju brenda 24 orëve.</p>
                    </div>
                  </motion.div>
                )}

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg flex items-center gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <p className="text-red-400">Ndodhi një gabim. Ju lutem provoni përsëri ose na kontaktoni direkt.</p>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Info */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="flex items-center gap-2 text-cream font-semibold mb-2">
                        <User className="w-4 h-4 text-gold-400" />
                        Emri i Plotë *
                      </label>
                      <input
                        type="text"
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Emri Mbiemri"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-cream font-semibold mb-2">
                        <Mail className="w-4 h-4 text-gold-400" />
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="flex items-center gap-2 text-cream font-semibold mb-2">
                        <Phone className="w-4 h-4 text-gold-400" />
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+355 69 XXX XXXX"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-cream font-semibold mb-2">
                        <Briefcase className="w-4 h-4 text-gold-400" />
                        Kompania (Opsionale)
                      </label>
                      <input
                        type="text"
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="Emri i Kompanisë"
                      />
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="pt-6 border-t border-gold-400/20">
                    <h3 className="text-xl font-display font-bold text-cream mb-4">Detajet e Projektit</h3>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-cream font-semibold mb-2">
                          Lloji i Projektit *
                        </label>
                        <select
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                          value={formData.projectType}
                          onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                        >
                          <option value="">Zgjidh llojin e projektit</option>
                          {projectTypes.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-cream font-semibold mb-2">
                            Buxheti *
                          </label>
                          <select
                            required
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                            value={formData.budget}
                            onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                          >
                            <option value="">Zgjidh buxhetin</option>
                            {budgetRanges.map((range) => (
                              <option key={range} value={range}>{range}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="flex items-center gap-2 text-cream font-semibold mb-2">
                            <Calendar className="w-4 h-4 text-gold-400" />
                            Afati Kohor *
                          </label>
                          <select
                            required
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50"
                            value={formData.timeline}
                            onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                          >
                            <option value="">Zgjidh afatin</option>
                            {timelineOptions.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-cream font-semibold mb-2">
                          <MessageSquare className="w-4 h-4 text-gold-400" />
                          Përshkrimi i Projektit *
                        </label>
                        <textarea
                          required
                          rows={6}
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors resize-none disabled:opacity-50"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="Na trego për projektin tënd... Çfarë dëshiron të krijosh? Cilat janë objektivat e tua?"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full btn-primary flex items-center justify-center gap-2 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{isSubmitting ? 'Duke dërguar...' : 'Dërgo Aplikimin'}</span>
                    <Send className={`w-5 h-5 ${isSubmitting ? 'animate-pulse' : ''}`} />
                  </button>

                  <p className="text-beige-200 text-sm text-center">
                    Duke dërguar aplikimin, pranon <a href="#" className="text-gold-400 hover:text-gold-300">Kushtet e Shërbimit</a> dhe <a href="#" className="text-gold-400 hover:text-gold-300">Politikën e Privatësisë</a>.
                  </p>
                </form>
              </div>
            </motion.div>

            {/* Info Cards */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-bronze-900/30 backdrop-blur-sm rounded-2xl p-6 border border-gold-400/20 text-center"
              >
                <div className="w-12 h-12 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-cream mb-2">Përgjigje e Shpejtë</h3>
                <p className="text-beige-200 text-sm">Konfirmim brenda 24 orëve</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-bronze-900/30 backdrop-blur-sm rounded-2xl p-6 border border-gold-400/20 text-center"
              >
                <div className="w-12 h-12 bg-gradient-bronze rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-cream mb-2">Konsultim Falas</h3>
                <p className="text-beige-200 text-sm">Takim online për diskutimin e projektit</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-bronze-900/30 backdrop-blur-sm rounded-2xl p-6 border border-gold-400/20 text-center"
              >
                <div className="w-12 h-12 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-cream mb-2">Plan i Detajuar</h3>
                <p className="text-beige-200 text-sm">Ofertë dhe timeline për projektin</p>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Apply
